import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { api, ApiError, currentEntity, Features, Me, setEntity } from './api';

interface Session {
  me: Me | null; features: Features | null; entityId: string | null; loading: boolean;
  switchEntity(id: string): void; reload(): Promise<void>; logout(): Promise<void>;
  role: string | null; on(flag: string): boolean;
}
const Ctx = createContext<Session>(null as any);
export const useSession = () => useContext(Ctx);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [features, setFeatures] = useState<Features | null>(null);
  const [entityId, setEntityId] = useState<string | null>(currentEntity());
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const m = await api<Me>('/me', { entity: false });
      setMe(m);
      let eid = currentEntity();
      if (!eid || !m.memberships.some((x) => x.entity_id === eid)) eid = m.memberships[0]?.entity_id ?? null;
      setEntity(eid); setEntityId(eid);
      setFeatures(await api<Features>('/features', { entity: false }));
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) console.error(e);
      setMe(null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const value: Session = {
    me, features, entityId, loading, reload,
    switchEntity(id) { setEntity(id); setEntityId(id); },
    async logout() { await api('/auth/logout', { method: 'POST', entity: false }); setMe(null); },
    role: me?.memberships.find((m) => m.entity_id === entityId)?.role ?? null,
    on: (flag) => !!features?.flags[flag]?.on,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
